/**
 * Platform Analytics landing shell — tab router + QueryClientProvider scope.
 *
 * Responsibilities:
 * - Own the React Query client for all analytics queries
 * - Own the shared date range
 * - Own the reveal-identifiers session toggle
 * - Route between the 7 tabs (Overview default)
 *
 * Every tab is a pure React component that receives `range` and `revealed`
 * as props and fetches its own data via useAnalyticsQuery.
 */
import React, { useMemo, useState } from 'react';
import {
  Activity,
  BarChart3,
  Compass,
  Gauge,
  Heart,
  Hourglass,
  Gift,
  LineChart,
  Settings,
  Sparkles,
} from 'lucide-react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { cn } from '../../../lib/cn';
import { adminFetch } from '../../../utils/adminApi';
import { useAnalyticsRange } from './hooks/useAnalyticsRange';
import { DateRangePicker } from './components/DateRangePicker';
import { RevealIdentifiersToggle } from './components/RevealIdentifiersToggle';
import { OverviewTab } from './tabs/OverviewTab';
import { FunnelTab } from './tabs/FunnelTab';
import { EngagementTab } from './tabs/EngagementTab';
import { RetentionTab } from './tabs/RetentionTab';
import { RelationshipTab } from './tabs/RelationshipTab';
import { RevenueTab } from './tabs/RevenueTab';
import { ProductTab } from './tabs/ProductTab';
import { TrialTab } from './tabs/TrialTab';
import { FreeStartTab } from './tabs/FreeStartTab';
import { SettingsTab } from './tabs/SettingsTab';

interface AdminMeResponse {
  success: boolean;
  admin: {
    admin_id: string;
    email: string;
    full_name: string;
    role: string;
    permissions: Record<string, unknown>;
  };
}

type TabKey =
  | 'overview'
  | 'trial'
  | 'free_start'
  | 'funnel'
  | 'engagement'
  | 'retention'
  | 'relationship'
  | 'revenue'
  | 'product'
  | 'settings';

interface TabDef {
  key: TabKey;
  label: string;
  icon: React.ElementType;
}

const TABS: TabDef[] = [
  { key: 'overview', label: 'Overview', icon: Gauge },
  { key: 'trial', label: 'Trial', icon: Hourglass },
  // Members who start without a card — excluded from Trial by design.
  { key: 'free_start', label: 'Free Start', icon: Gift },
  { key: 'funnel', label: 'Funnel', icon: Compass },
  { key: 'engagement', label: 'Engagement', icon: Activity },
  { key: 'retention', label: 'Retention', icon: LineChart },
  { key: 'relationship', label: 'Relationship', icon: Heart },
  { key: 'revenue', label: 'Revenue', icon: BarChart3 },
  { key: 'product', label: 'Product', icon: Sparkles },
  { key: 'settings', label: 'Analytics Settings', icon: Settings },
];

// One QueryClient per mount is fine — admin pages are long-lived single-screen sessions.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const PlatformAnalyticsDashboard: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Inner />
    </QueryClientProvider>
  );
};

const Inner: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const range = useAnalyticsRange('30d');
  const [revealed, setRevealed] = useState(false);

  // Pull the admin role from /api/admin/auth/me so the reveal toggle can gate.
  const { data: meData } = useQuery<AdminMeResponse>({
    queryKey: ['admin-me'],
    queryFn: () => adminFetch<AdminMeResponse>('/api/admin/auth/me'),
    staleTime: 15 * 60 * 1000,
    retry: false,
  });
  const isSuperAdmin = meData?.admin?.role === 'super_admin';

  const tabContent = useMemo(() => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab range={range} />;
      case 'trial':
        return <TrialTab range={range} />;
      case 'free_start':
        return <FreeStartTab revealed={revealed} isSuperAdmin={isSuperAdmin} />;
      case 'funnel':
        return <FunnelTab range={range} />;
      case 'engagement':
        return <EngagementTab range={range} />;
      case 'retention':
        return <RetentionTab range={range} />;
      case 'relationship':
        return <RelationshipTab range={range} revealed={revealed} isSuperAdmin={isSuperAdmin} />;
      case 'revenue':
        return <RevenueTab range={range} />;
      case 'product':
        return <ProductTab range={range} />;
      case 'settings':
        return <SettingsTab />;
      default:
        return null;
    }
  }, [activeTab, range, revealed, isSuperAdmin]);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'League Spartan', sans-serif", color: 'var(--cedar-ink)' }}>Platform Analytics</h1>
          <p className="mt-1 text-sm text-gray-500">
            Sessions, retention, coaching relationship depth, and the full acquisition funnel
            — all rolled up nightly.
            {' '}
            <span className="font-medium text-gray-600">All times anchored on Eastern (America/New_York).</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangePicker range={range} />
          {activeTab === 'relationship' && (
            <RevealIdentifiersToggle
              revealed={revealed}
              onChange={setRevealed}
              isSuperAdmin={isSuperAdmin}
            />
          )}
        </div>
      </header>

      {/* Tab bar */}
      <nav className="flex flex-wrap items-center gap-1 rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors',
                active
                  ? 'bg-coach-cedar text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100',
              )}
              aria-current={active ? 'page' : undefined}
            >
              <Icon className="h-4 w-4" aria-hidden />
              {t.label}
            </button>
          );
        })}
      </nav>

      {/* Tab content */}
      <div>{tabContent}</div>
    </div>
  );
};

export default PlatformAnalyticsDashboard;
